import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Switch } from "../components/ui/switch";
import KofiModal from "../components/KofiModal";
import FriendsTab from "../components/FriendsTab";
import CharacterManagement from "../components/CharacterManagement";
import { 
  ArrowLeft, 
  User, 
  Upload, 
  Loader2,
  Camera,
  Lock,
  Eye,
  EyeOff,
  Heart,
  Check,
  X,
  Pencil,
  Users,
  Settings,
  Globe,
  UserCheck,
  Shield,
  MessageCircle,
  Sword,
  Trophy
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [avatar, setAvatar] = useState(user?.avatar || null);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("profile");
  
  // Username change state
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState(user?.username || "");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Privacy settings state
  const [profilePrivacy, setProfilePrivacy] = useState(user?.profile_privacy || "public");
  const [hideFromRankings, setHideFromRankings] = useState(Boolean(user?.hide_from_rankings));
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  // Ko-fi Modal
  const [showKofiModal, setShowKofiModal] = useState(false);

  // Check if user is OAuth user (Google/Apple)
  const isOAuthUser = user?.auth_provider === "google" || user?.auth_provider === "apple" || user?.auth_provider === "discord";

  // Load privacy settings
  useEffect(() => {
    const loadPrivacy = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/user/privacy`);
        setProfilePrivacy(response.data.profile_privacy);
        setHideFromRankings(Boolean(response.data.hide_from_rankings));
      } catch (error) {
        console.error("Failed to load privacy settings:", error);
      }
    };
    if (user) {
      loadPrivacy();
    }
  }, [user]);

  // Debounced username check
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      setUsernameError(username.length > 0 ? "Username must be at least 3 characters" : "");
      return;
    }
    
    if (username === user?.username) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    // Validate format
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameAvailable(false);
      setUsernameError("Only letters, numbers, and underscores allowed");
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/check-username`, { username });
      setUsernameAvailable(response.data.available);
      setUsernameError(response.data.available ? "" : response.data.reason);
    } catch (error) {
      setUsernameAvailable(null);
      setUsernameError("Failed to check availability");
    } finally {
      setCheckingUsername(false);
    }
  }, [user?.username]);

  // Debounce effect for username check
  useEffect(() => {
    if (!editingUsername) return;
    
    const timer = setTimeout(() => {
      checkUsernameAvailability(newUsername);
    }, 500);

    return () => clearTimeout(timer);
  }, [newUsername, editingUsername, checkUsernameAvailability]);

  if (!user) {
    navigate("/");
    return null;
  }

  const handleUsernameChange = async () => {
    if (!usernameAvailable || newUsername === user.username) {
      toast.error("Please choose a valid, available username");
      return;
    }

    setSavingUsername(true);
    try {
      const response = await axios.put(`${API_URL}/api/auth/profile`, {
        username: newUsername
      });
      
      if (response.data.success) {
        updateUser({ ...user, username: newUsername });
        toast.success("Username updated successfully!");
        setEditingUsername(false);
        setUsernameAvailable(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update username");
    } finally {
      setSavingUsername(false);
    }
  };

  const cancelUsernameEdit = () => {
    setEditingUsername(false);
    setNewUsername(user.username);
    setUsernameAvailable(null);
    setUsernameError("");
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error("Image must be smaller than 500KB");
      return;
    }

    setUploading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        
        try {
          await axios.put(`${API_URL}/api/auth/profile`, {
            avatar: base64
          });
          
          setAvatar(base64);
          updateUser({ ...user, avatar: base64 });
          toast.success("Avatar updated!");
        } catch (error) {
          toast.error("Failed to upload avatar");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to process image");
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, {
        avatar: null
      });
      setAvatar(null);
      updateUser({ ...user, avatar: null });
      toast.success("Avatar removed");
    } catch (error) {
      toast.error("Failed to remove avatar");
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully!");
      setShowPasswordForm(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePrivacyChange = async (value) => {
    setSavingPrivacy(true);
    try {
      await axios.put(`${API_URL}/api/user/privacy`, {
        profile_privacy: value
      });
      setProfilePrivacy(value);
      updateUser({ ...user, profile_privacy: value });
      toast.success("Privacy settings updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update privacy settings");
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleHideFromRankingsChange = async (checked) => {
    setSavingPrivacy(true);
    try {
      await axios.put(`${API_URL}/api/user/privacy`, {
        hide_from_rankings: checked,
      });
      setHideFromRankings(checked);
      updateUser({ ...user, hide_from_rankings: checked });
      toast.success(checked ? "Hidden from rankings" : "Visible on rankings");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update ranking visibility");
    } finally {
      setSavingPrivacy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Profile Settings</h1>
              <p className="text-sm text-muted-foreground">Manage your account and friends</p>
            </div>
          </div>
          {user.is_kofi_supporter && (
            <div className="flex items-center gap-2 px-3 py-1 bg-amber-500/20 rounded-full">
              <Heart className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-sm font-medium text-amber-400">Ko-fi Supporter</span>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile" className="gap-2">
              <Settings className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="characters" className="gap-2">
              <Sword className="w-4 h-4" />
              Characters
            </TabsTrigger>
            <TabsTrigger value="friends" className="gap-2">
              <Users className="w-4 h-4" />
              Friends
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            {/* Avatar Section */}
            <Card className="bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Profile Picture
                </CardTitle>
                <CardDescription>
                  Upload an avatar that will be displayed next to your kills
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  {/* Avatar Preview */}
                  <div className="relative">
                    {avatar ? (
                      <img 
                        src={avatar} 
                        alt={user.username} 
                        className="w-24 h-24 rounded-full object-cover border-4 border-border"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center border-4 border-border">
                        <User className="w-10 h-10 text-primary" />
                      </div>
                    )}
                    
                    {/* Upload overlay */}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="absolute inset-0 rounded-full bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      ) : (
                        <Upload className="w-6 h-6 text-white" />
                      )}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                      data-testid="avatar-upload-input"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-avatar-button"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload New Avatar
                    </Button>
                    {avatar && (
                      <Button 
                        variant="ghost" 
                        onClick={removeAvatar}
                        disabled={uploading}
                        className="text-destructive hover:text-destructive"
                      >
                        Remove Avatar
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Max size: 500KB. Supported: JPG, PNG, GIF
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Info */}
            <Card className="bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Account Information
                </CardTitle>
                {isOAuthUser && (
                  <CardDescription>
                    Signed in with {user.auth_provider === "google" ? "Google" : user.auth_provider === "discord" ? "Discord" : "Apple"}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Username</Label>
                  {editingUsername ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input 
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value)}
                            maxLength={30}
                            className={usernameAvailable === false ? "border-destructive" : usernameAvailable === true ? "border-green-500" : ""}
                            data-testid="username-input"
                          />
                          {checkingUsername && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                          )}
                          {!checkingUsername && usernameAvailable === true && (
                            <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                          )}
                          {!checkingUsername && usernameAvailable === false && (
                            <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-destructive" />
                          )}
                        </div>
                        <Button 
                          onClick={handleUsernameChange}
                          disabled={savingUsername || !usernameAvailable || newUsername === user.username}
                          data-testid="save-username-button"
                        >
                          {savingUsername ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                        </Button>
                        <Button variant="outline" onClick={cancelUsernameEdit}>
                          Cancel
                        </Button>
                      </div>
                      {usernameError && (
                        <p className="text-xs text-destructive">{usernameError}</p>
                      )}
                      {usernameAvailable && (
                        <p className="text-xs text-green-500">Username is available!</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={user.username} disabled className="flex-1" />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => {
                          setEditingUsername(true);
                          setNewUsername(user.username);
                        }}
                        title="Edit username"
                        data-testid="edit-username-button"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user.email} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Member Since</Label>
                  <Input 
                    value={new Date(user.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric"
                    })} 
                    disabled 
                  />
                </div>
              </CardContent>
            </Card>

            {/* Privacy Settings */}
            <Card className="bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Privacy Settings
                </CardTitle>
                <CardDescription>
                  Control who can view your statistics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={profilePrivacy}
                  onValueChange={handlePrivacyChange}
                  disabled={savingPrivacy}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-background/50 border border-border/50">
                    <RadioGroupItem value="public" id="public" />
                    <Label htmlFor="public" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-green-400" />
                        <span className="font-medium">Public</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Anyone can view your statistics
                      </p>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-background/50 border border-border/50">
                    <RadioGroupItem value="friends_only" id="friends_only" />
                    <Label htmlFor="friends_only" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-blue-400" />
                        <span className="font-medium">Friends Only</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Only your friends can view your statistics
                      </p>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-background/50 border border-border/50">
                    <RadioGroupItem value="private" id="private" />
                    <Label htmlFor="private" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-red-400" />
                        <span className="font-medium">Private</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Nobody can view your statistics
                      </p>
                    </Label>
                  </div>
                </RadioGroup>
                {savingPrivacy && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </div>
                )}

                {/* Hide-from-rankings toggle */}
                <div className="flex items-start gap-3 p-3 mt-3 rounded-lg bg-background/50 border border-border/50">
                  <Trophy className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <Label
                        htmlFor="hide-rankings-toggle"
                        className="font-medium cursor-pointer"
                      >
                        Hide me from the public Ranking
                      </Label>
                      <Switch
                        id="hide-rankings-toggle"
                        checked={hideFromRankings}
                        onCheckedChange={handleHideFromRankingsChange}
                        disabled={savingPrivacy}
                        data-testid="hide-from-rankings-toggle"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      When on, your name will not appear on{" "}
                      <span className="text-amber-300 font-bold">OreBossTracker Ranking</span>
                      {" "}or any boss leaderboard. Your kills still count for your own personal stats.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chat Settings */}
            <Card className="bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Chat Settings
                </CardTitle>
                <CardDescription>
                  Control the global chat appearance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50">
                  <div>
                    <p className="font-medium">Enable Global Chat</p>
                    <p className="text-xs text-muted-foreground">Show the chat on all pages (completely hidden when disabled)</p>
                  </div>
                  <Switch
                    checked={localStorage.getItem("shoutbox_enabled") !== "false"}
                    onCheckedChange={(checked) => {
                      localStorage.setItem("shoutbox_enabled", checked.toString());
                      window.location.reload();
                    }}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50">
                  <div>
                    <p className="font-medium">Sound Notifications</p>
                    <p className="text-xs text-muted-foreground">Play sound when new messages arrive</p>
                  </div>
                  <Switch
                    checked={localStorage.getItem("shoutbox_sound") !== "false"}
                    onCheckedChange={(checked) => {
                      localStorage.setItem("shoutbox_sound", checked.toString());
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Chat Size</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: "single", label: "Single" },
                      { value: "small", label: "Small" },
                      { value: "medium", label: "Medium" },
                      { value: "large", label: "Large" },
                    ].map((size) => (
                      <Button
                        key={size.value}
                        variant={localStorage.getItem("shoutbox_size") === size.value || (!localStorage.getItem("shoutbox_size") && size.value === "medium") ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          localStorage.setItem("shoutbox_size", size.value);
                          window.location.reload();
                        }}
                      >
                        {size.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Password Change Section - Only show for non-OAuth users */}
            {!isOAuthUser && (
            <Card className="bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Password
                </CardTitle>
                <CardDescription>
                  Change your account password
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!showPasswordForm ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPasswordForm(true)}
                    data-testid="change-password-button"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                ) : (
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          minLength={6}
                          data-testid="new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={changingPassword}>
                        {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save New Password
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setShowPasswordForm(false);
                          setCurrentPassword("");
                          setNewPassword("");
                          setConfirmPassword("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
            )}

            {/* Quick Links */}
            <Card className="bg-card/50">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => navigate("/stats")}
                  >
                    View Statistics
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="flex-1"
                    onClick={logout}
                  >
                    Logout
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Ko-fi Support */}
            <Card className="bg-[#FF5E5B]/5 border-[#FF5E5B]/20">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center">
                  <Heart className="w-8 h-8 text-[#FF5E5B] fill-[#FF5E5B] mb-3" />
                  <h3 className="font-semibold text-[#FF5E5B] mb-2">Support the Project</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Help keep the servers running and support development!
                  </p>
                  <Button 
                    onClick={() => setShowKofiModal(true)}
                    className="bg-[#FF5E5B] hover:bg-[#FF5E5B]/90 text-white"
                  >
                    <Heart className="w-4 h-4 mr-2 fill-white" />
                    Support on Ko-fi
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Characters Tab */}
          <TabsContent value="characters">
            <CharacterManagement />
          </TabsContent>

          {/* Friends Tab */}
          <TabsContent value="friends">
            <FriendsTab />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Ko-fi Modal */}
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
    </div>
  );
};

export default ProfilePage;
